import React, { useEffect } from "react";

import { ProductEventProps, productAnalytics, trackMixpanel, useAppType, useGeneralContext, useTranslation, useUserContext } from "utils";

import { Crumb, ErrorBoundary, InformationCard, MetaData, PageSpin } from "components";

export type NavigationCrumbs = {
  crumbs: Crumb[];
  additionalData?: React.ReactNode;
};

type MainPageTypes = ProductEventProps & {
  pageName: string;
  afterPage?: React.ReactNode;
  beforePage?: React.ReactNode;
  children: React.ReactNode;
  containerClass?: string;
  fullWidth?: boolean;
  loading?: boolean;
  dataTestId?: string;
  className?: string;
  navigationCrumbs?: NavigationCrumbs;
  share?: { title: string };
  showMobileAlert?: boolean;
  /** How the user reached this page. We register it once per session as `entry_point`, so later events carry it. */
  entryPoint?: string;
};

export const MainPage = ({
  afterPage,
  beforePage,
  children,
  containerClass = "",
  fullWidth,
  loading,
  pageName,
  dataTestId,
  className,
  navigationCrumbs = { crumbs: [], additionalData: <></> },
  share,
  showMobileAlert,
  entryPoint,
  productEvent,
  productEventProperties,
}: MainPageTypes) => {
  const { user } = useUserContext();
  const { mobile } = useAppType();
  const { railNavCollapsed, setFullWidthPage, setNavigationCrumbs } = useGeneralContext();

  useEffect(() => {
    trackMixpanel("OpenPage", { "page url": window.location.pathname });
  }, [window.location.pathname]); // eslint-disable-line

  useEffect(() => {
    if (entryPoint) productAnalytics.setSessionPropertyOnce("entry_point", entryPoint);
  }, [entryPoint]);

  useEffect(() => {
    if (productEvent) productAnalytics.track(productEvent, productEventProperties);
    // We omit the properties from the deps. An inline object would send the page event again on each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productEvent]);

  useEffect(() => {
    if (!navigationCrumbs.crumbs.length) return;
    setNavigationCrumbs?.(navigationCrumbs);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      setNavigationCrumbs?.({ crumbs: [], additionalData: <></> });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!setFullWidthPage || fullWidth === undefined) return;
    setFullWidthPage(!!fullWidth);

    return () => {
      if (setFullWidthPage) setFullWidthPage(false);
    };
  }, [fullWidth, setFullWidthPage]);

  const classNames = `${fullWidth ? "full-width-main-page" : "container"} ${railNavCollapsed ? "" : "menu-open"} content ${user ? "" : "anon-page"} ${
    user || containerClass.includes("margin-top-0") ? "" : "margin-top-250"
  } ${containerClass || ""}`;

  const renderContent = () => {
    if (loading || !children) return <PageSpin />;
    return mobile && showMobileAlert ? <MobileAlert /> : children;
  };

  return (
    <>
      {share && <MetaData title={share.title} />}
      {beforePage}
      <section className={`page ${pageName} ${user && mobile ? "in-app" : ""}`} data-testid={dataTestId}>
        <section className={className ?? classNames}>
          <ErrorBoundary>{renderContent()}</ErrorBoundary>
        </section>
      </section>
      {afterPage}
    </>
  );
};

const MobileAlert = () => {
  const { t } = useTranslation("global");

  return (
    <div className="padding-100">
      <InformationCard variant="neutral" title={t("mobileAlert.title")} text={t("mobileAlert.description")} iconName="tabler/info-circle" />
    </div>
  );
};
